import { PREFECTURES } from "./constants";
import { normalizeSearchText } from "./formatters";
import type { EventSnapshot, Gender, LocationKind, NormalizedRunner } from "../types";
import type { RawRunner, RawSnapshot, SourceMetadata } from "./schemas";

export function getAgeGroup(age: number | null | undefined): string {
  if (age == null || age <= 0 || !Number.isFinite(age) || age > 120) return "未知";
  if (age <= 19) return "19 歲以下";
  if (age <= 29) return "20–29 歲";
  if (age <= 39) return "30–39 歲";
  if (age <= 49) return "40–49 歲";
  if (age <= 59) return "50–59 歲";
  if (age <= 69) return "60–69 歲";
  if (age <= 79) return "70–79 歲";
  return "80 歲以上";
}

export function getGender(code: number | null | undefined): {
  value: Gender;
  label: string;
} {
  if (code === 1) return { value: "male", label: "男性" };
  if (code === 2) return { value: "female", label: "女性" };
  if (code === 9) return { value: "other", label: "其他" };
  return { value: "unknown", label: "未知／未公開" };
}

export function getLocation(code: number | null | undefined): {
  kind: LocationKind;
  label: string;
  code: number | null;
} {
  if (code != null && code >= 1 && code <= 47) {
    return { kind: "japan", label: PREFECTURES[code - 1], code };
  }
  if (code === 99) return { kind: "overseas", label: "海外", code };
  return { kind: "unknown", label: "未知／未公開", code: null };
}

function normalizeRunner(raw: RawRunner, eventKey: string, maximumSourceOrder: number): NormalizedRunner {
  const age = raw.age != null && raw.age > 0 && raw.age <= 120 ? raw.age : null;
  const gender = getGender(raw.gender);
  const location = getLocation(raw.prefectureCode);
  const achievedAtEpoch = Math.floor(raw.achievedDate);
  const fallbackKey = [
    eventKey,
    achievedAtEpoch,
    raw.totalTime,
    raw.activityCount,
    raw.order,
  ].join(":");

  return {
    recordKey: raw.userId > 0 ? `${eventKey}:${raw.userId}` : fallbackKey,
    userId: raw.userId,
    sourceOrder: raw.order,
    achievementOrder: maximumSourceOrder - raw.order + 1,
    nickname: raw.nickname,
    searchNickname: normalizeSearchText(raw.nickname),
    age,
    ageGroup: getAgeGroup(raw.age),
    gender: gender.value,
    genderLabel: gender.label,
    prefectureCode: location.code,
    locationKind: location.kind,
    locationLabel: location.label,
    achievedAt: new Date(achievedAtEpoch).toISOString(),
    achievedAtEpoch,
    totalTimeSeconds: raw.totalTime,
    activityCount: raw.activityCount,
  };
}

function findOrderIssues(runners: RawRunner[]) {
  const counts = new Map<number, number>();
  for (const runner of runners) counts.set(runner.order, (counts.get(runner.order) ?? 0) + 1);
  const duplicateOrders = [...counts]
    .filter(([, count]) => count > 1)
    .map(([order]) => order)
    .sort((a, b) => a - b);
  const missingOrders: number[] = [];
  const maxOrder = Math.max(0, ...counts.keys());
  for (let order = 1; order <= maxOrder; order += 1) {
    if (!counts.has(order)) missingOrders.push(order);
  }
  return { duplicateOrders, missingOrders };
}

export function normalizeSnapshot(
  reference: { eventKey: string; raceId: number; rankingType: "distance-challenge"; sourceUrl: string },
  metadata: SourceMetadata,
  snapshot: RawSnapshot,
  fetchedAt = new Date(),
): EventSnapshot {
  if (snapshot.achieverCount !== snapshot.achieverList.length) {
    throw new Error(
      `達標人數 ${snapshot.achieverCount} 與資料列數 ${snapshot.achieverList.length} 不一致。`,
    );
  }

  const unknownGenderCodes = new Set<number>();
  const unknownLocationCodes = new Set<number>();
  let missingAge = 0;
  let missingGender = 0;
  let missingLocation = 0;
  let overseasCount = 0;
  let privateUserCount = 0;
  let invalidAgeCount = 0;

  for (const runner of snapshot.achieverList) {
    if (runner.age == null || runner.age <= 0 || runner.age > 120) missingAge += 1;
    if (runner.age != null && (runner.age <= 0 || runner.age > 120)) invalidAgeCount += 1;
    if (![1, 2, 9].includes(runner.gender ?? 0)) {
      missingGender += 1;
      if (runner.gender != null && runner.gender !== 0) unknownGenderCodes.add(runner.gender);
    }
    if (runner.prefectureCode == null || ![...Array.from({ length: 47 }, (_, index) => index + 1), 99].includes(runner.prefectureCode)) {
      missingLocation += 1;
      if (runner.prefectureCode != null) unknownLocationCodes.add(runner.prefectureCode);
    }
    if (runner.prefectureCode === 99) overseasCount += 1;
    if (runner.userId === -1) privateUserCount += 1;
  }

  const orderIssues = findOrderIssues(snapshot.achieverList);
  const maximumSourceOrder = Math.max(0, ...snapshot.achieverList.map((runner) => runner.order));
  return {
    eventKey: reference.eventKey,
    raceId: reference.raceId,
    rankingType: reference.rankingType,
    title: metadata.race.title,
    description: metadata.race.event_description ?? "",
    targetDistance: snapshot.targetDistance ?? null,
    participantCount: metadata.race.ranking_user_count ?? null,
    achieverCount: snapshot.achieverCount,
    eventStartAt: new Date(metadata.race.beginning_ranking_term).toISOString(),
    eventEndAt: new Date(metadata.race.end_of_ranking_term).toISOString(),
    aggregateAt: new Date(metadata.aggregateDateEpochMsec).toISOString(),
    fetchedAt: fetchedAt.toISOString(),
    sourceUrl: reference.sourceUrl,
    runners: snapshot.achieverList.map((runner) => normalizeRunner(runner, reference.eventKey, maximumSourceOrder)),
    quality: {
      rawCount: snapshot.achieverList.length,
      validCount: snapshot.achieverList.length,
      missingAge,
      missingGender,
      missingLocation,
      overseasCount,
      privateUserCount,
      invalidAgeCount,
      unknownGenderCodes: [...unknownGenderCodes].sort((a, b) => a - b),
      unknownLocationCodes: [...unknownLocationCodes].sort((a, b) => a - b),
      countMatches: true,
      ...orderIssues,
    },
  };
}
